import { z } from "zod";

/**
 * URL search params for `/$workspaceSlug/recruitment/interviews`.
 *
 * The enum is spelled out here rather than imported from
 * `interview.schema.ts`, and that is a bundle decision, not an oversight:
 * `router.tsx` imports this module EAGERLY for `validateSearch`, so anything
 * it reaches lands in the initial chunk. Four literals cost nothing; the
 * schema module they came from would drag the whole interview contract in
 * (P1). The two lists are kept in step by `interviews.route.search.test.ts`.
 */
const STATUSES = ["pending", "under_review", "cleared", "rejected"] as const;

export const interviewsRouteSearchSchema = z
  .object({
    scope: z.enum(["own", "workspace"]).optional(),
    status: z.enum(STATUSES).optional(),
    /// `YYYY-MM-DD` bounds on the interview date, both inclusive.
    from: z.string().optional(),
    to: z.string().optional(),
    /// Set when one interview is open. In the URL rather than in `useState`
    /// (S3) because "the interview I am about to give feedback on" is a link
    /// somebody sends, and Back should close the panel rather than leave the
    /// page.
    interviewId: z.string().optional(),
  })
  .strict();

export type InterviewsRouteSearch = z.infer<typeof interviewsRouteSearchSchema>;
export type InterviewScope = NonNullable<InterviewsRouteSearch["scope"]>;

/**
 * Which lens the URL means, for a given viewer.
 *
 * `own` is the default for everyone, admins included: this route is reached
 * from a card or a reminder about your own panel seat, and the workspace lens
 * is one click away.
 *
 * A `?scope=workspace` link forwarded to somebody without the scheduling key
 * resolves back to `own` rather than rendering a tab the shore would 403.
 * That is design decision 12 made literal: an interviewer arriving here is
 * passed THROUGH to their own rows, never stopped at an `AdminOnlyCard`. The
 * referrals route normalises its scope the same way.
 */
export function resolveInterviewScope(
  search: InterviewsRouteSearch,
  canScheduleWorkspace: boolean,
): InterviewScope {
  if (!canScheduleWorkspace) return "own";
  return search.scope ?? "own";
}
